import type { EChartsOption } from "echarts";
import mustache from "mustache";
import { EChartRender } from "../../../../implementations/chart/EChartRender";
import { CompraAnimalesResponse } from "../../../../../modulos/pesaje/ganaderia/response/CompraAnimalesResponse";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  gdpEsperadoKgDia,
  gdpRealKgDia,
  pesoPromedioEstimado,
  pesoPromedioReal,
  pesoTargetSalidaPromedio,
} from "../../../../../utils/ganaderiaUtils";
import template from "./proyeccionSalidaCardTemplate.html";

mustache.parse(template);

/** Curva de proyección de un lote: peso inicial, GDP diario y días hasta
 *  alcanzar el target (peso ya topado en el target). */
type SerieLote = {
  nombre: string;
  puntos: number[];
  target: number;
};

/** Card "Proyección de salida por lote": una línea por lote activo, del peso
 *  actual (real si hay pesaje, si no estimado) hacia el peso objetivo
 *  promedio de sus compras activas, a la velocidad del GDP (real si hay
 *  pesaje, si no el esperado ponderado de esas mismas compras). Lotes sin
 *  peso inicial, GDP o target válidos no tienen línea. */
export class ProyeccionSalidaCardRender extends EChartRender {
  private readonly _series: SerieLote[];

  constructor(
    lotes: ClienteFincaLoteResponse[],
    private readonly _compras: CompraAnimalesResponse[],
  ) {
    super();
    this._series = lotes
      .filter((l) => l.ganaderiaCantidadAnimales > 0)
      .map((l) => this._serieDeLote(l))
      .filter((s): s is SerieLote => s != null);
  }

  override render(): string {
    return mustache.render(template, {
      hasDatos: this._series.length > 0,
      chart: super.render(),
    });
  }

  private _serieDeLote(lote: ClienteFincaLoteResponse): SerieLote | null {
    const pesoInicial = pesoPromedioReal(lote) ?? pesoPromedioEstimado(lote);
    const gdp = gdpRealKgDia(lote, this._compras) ?? gdpEsperadoKgDia(lote, this._compras);
    const target = pesoTargetSalidaPromedio(lote, this._compras);
    if (pesoInicial == null || gdp == null || gdp <= 0 || target == null || target <= pesoInicial) {
      return null;
    }

    const dias = Math.ceil((target - pesoInicial) / gdp);
    const puntos: number[] = [];
    for (let d = 0; d <= dias; d++) {
      puntos.push(Math.min(pesoInicial + gdp * d, target));
    }
    return { nombre: lote.nombreLote, puntos, target };
  }

  protected getOption(): EChartsOption {
    const diasMax = Math.max(0, ...this._series.map((s) => s.puntos.length - 1));
    const categorias = Array.from({ length: diasMax + 1 }, (_, i) => `Día +${i}`);

    const targets = this._series.map((s) => s.target);
    const targetsIguales =
      targets.length > 0 && targets.every((t) => Math.abs(t - targets[0]) < 1);

    const series = this._series.map((s, i) => {
      const relleno = Array(diasMax + 1 - s.puntos.length).fill(
        s.puntos[s.puntos.length - 1],
      );
      return {
        name: s.nombre,
        type: "line" as const,
        data: [...s.puntos, ...relleno],
        showSymbol: false,
        ...(i === 0 && targetsIguales
          ? {
              markLine: {
                silent: true,
                symbol: "none" as const,
                lineStyle: { type: "dashed" as const },
                label: { formatter: `Target: ${Math.round(targets[0])} kg` },
                data: [{ yAxis: targets[0] }],
              },
            }
          : {}),
      };
    });

    return {
      tooltip: { trigger: "axis", valueFormatter: (v) => `${v} kg` },
      legend: { bottom: 0 },
      grid: { left: 48, right: 16, top: 24, bottom: 40 },
      xAxis: { type: "category", data: categorias, boundaryGap: false },
      yAxis: { type: "value", axisLabel: { formatter: "{value} kg" } },
      series,
    };
  }
}
